import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useCallback } from 'react';
import { Button, Table, useLocalizeMessage } from 'libs.nucleus';
import { AddEditDimensionModal } from './add_edit_dimension_modal.component';
import { useDimensionsColumns } from './dimensions_columns.hook';
import { EmptyDimensionsState } from './empty_dimensions_state.component';
import { useDataViews } from '../../hooks';
export const DimensionsTab = ({ dataView, onDataViewUpdate, isLoading = false }) => {
    const translate = useLocalizeMessage();
    const { addDimensions, updateDimensions, isLoading: isApiLoading } = useDataViews();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const dimensions = useMemo(() => {
        if (!dataView.data?.dimensions) {
            return [];
        }
        return Object.entries(dataView.data.dimensions).map(([key, dimension]) => ({
            id: key,
            name: key,
            title: dimension.title,
            type: dimension.type,
            description: dimension.description,
            sql: dimension.sql,
        }));
    }, [dataView.data?.dimensions]);
    const handleAddDimension = useCallback(() => {
        setIsEditMode(false);
        setIsModalOpen(true);
    }, []);
    const handleEditDimensions = useCallback(() => {
        setIsEditMode(true);
        setIsModalOpen(true);
    }, []);
    const handleSaveDimensions = useCallback(async (dimensions, sqlQuery) => {
        try {
            const dimensionsForm = dimensions.map((dimension) => ({
                name: dimension.name,
                type: dimension.type,
                sql: dimension.sql,
                description: undefined,
                title: dimension.title, // Preserve the friendly display name
            }));
            // Use updateDimensions for edit mode, addDimensions for create mode
            const updatedView = isEditMode
                ? await updateDimensions(dataView.id, dimensionsForm, sqlQuery, dataView)
                : await addDimensions(dataView.id, dimensionsForm, sqlQuery, dataView);
            onDataViewUpdate(updatedView);
            setIsModalOpen(false);
            setIsEditMode(false);
        }
        catch (error) {
            // Error already handled in hook
        }
    }, [dataView, addDimensions, updateDimensions, onDataViewUpdate, isEditMode]);
    const dimensionsColumns = useDimensionsColumns();
    const isEmpty = !isLoading && !isApiLoading && dimensions.length === 0;
    // Users can only add/edit dimensions when the view has a status of 'Draft' (SSP-3031)
    const isEditDisabled = dataView.status !== 'DRAFT' || isLoading || isApiLoading;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: 'mt-6', children: isEmpty ? (_jsx(EmptyDimensionsState, { onCreateDimension: handleAddDimension, disabled: isEditDisabled })) : (_jsx(Table, { data: dimensions, columns: dimensionsColumns, labelNoResults: translate('No dimensions found'), isLoading: isLoading || isApiLoading, enableSorting: true, useInternalSearch: true, labelSearchField: translate('Search'), customHeaderContent: _jsx(Button, { label: translate('Edit'), onClick: handleEditDimensions, variant: 'primary', disabled: isEditDisabled, className: 'ml-auto' }) })) }), isModalOpen && (_jsx(AddEditDimensionModal, { isOpen: isModalOpen, onClose: () => {
                    setIsModalOpen(false);
                    setIsEditMode(false);
                }, onSave: handleSaveDimensions, dataView: dataView, isLoading: isApiLoading, isEditMode: isEditMode }))] }));
};
